import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, X } from "@phosphor-icons/react";

export default function ReturnPrint() {
  const { returnId } = useParams();
  const navigate = useNavigate();
  const [ret, setRet] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        // Backend has no single-get endpoint — pull the full list and pick this one.
        const { data } = await api.get("/returns");
        const r = data.find(x => x.id === returnId);
        if (!r) { setError("Return not found"); return; }
        setRet(r);
        setTimeout(() => window.print(), 350);
      } catch (e) {
        setError("Could not load this return.");
      }
    };
    load();
  }, [returnId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-8">
        <div className="rounded-md border tf-hairline bg-white p-8 text-center max-w-md">
          <div className="text-lg font-[Chivo] font-black text-[#D32F2F]">{error}</div>
          <Button className="mt-4" onClick={() => navigate("/dashboard/returns")}>Back to returns</Button>
        </div>
      </div>
    );
  }

  if (!ret) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 print:py-0 print:bg-white return-print-page">
      {/* Screen-only controls */}
      <div className="no-print max-w-3xl mx-auto mb-6 flex items-center justify-between px-6">
        <div className="text-slate-600 text-sm">Print preview — this page will open your printer dialog automatically.</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}><X size={14} className="mr-1" /> Close</Button>
          <Button size="sm" className="bg-[#0A2540]" onClick={() => window.print()}><Printer size={14} className="mr-1" /> Print</Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none p-12 print:p-8 return-print-doc">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-black pb-4">
          <div>
            <div className="text-2xl font-black uppercase tracking-widest text-[#0A2540]">Taxfree Filer LLP</div>
            <div className="text-xs text-slate-600 mt-1">taxfreefiler.com · info@taxfreefiler.com · +91 (738) 672-4363</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Document</div>
            <div className="text-base font-bold text-[#0A2540]">Tax Return Cover Sheet</div>
            <div className="text-xs text-slate-500 mt-1">Reference #{ret.id.slice(0, 8).toUpperCase()}</div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mt-8">
          <h1 className="text-2xl font-black text-[#0A2540]">Tax Year {ret.tax_year}</h1>
          <div className="mt-2 text-sm text-slate-600">Return Type: <strong className="text-[#0A2540]">{ret.return_type}</strong></div>
        </div>

        {/* Meta Grid */}
        <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <Row label="Client" value={ret.client_name || ret.owner_name} />
          <Row label="Filing Status" value={(ret.filing_status || "—").toUpperCase()} />
          <Row label="Status" value={(ret.status || "—").replace("_", " ").toUpperCase()} />
          <Row label="Assigned Preparer" value={ret.assigned_to_name || "—"} />
          <Row label="Due Date" value={ret.due_date || "—"} />
          <Row label="Progress" value={`${ret.progress || 0}%`} />
        </div>

        {/* Financials */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-300 pb-1 mb-3">Financial Summary</div>
          <table className="w-full text-sm">
            <tbody>
              <FinRow label="Tax Liability" value={ret.tax_liability} tone="text-[#0A2540]" />
              <FinRow label="Balance Due" value={ret.balance_due} tone="text-[#D32F2F]" />
              <FinRow label="Refund Amount" value={ret.refund_amount} tone="text-emerald-700" />
            </tbody>
          </table>
        </div>

        {/* Notes */}
        {ret.notes && (
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-300 pb-1 mb-3">Preparer Notes</div>
            <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{ret.notes}</div>
          </div>
        )}

        {/* Timeline */}
        <div className="mt-10 text-xs text-slate-600 space-y-1">
          <div>Created: <span className="font-mono">{(ret.created_at || "").replace("T", " ").slice(0, 19)}</span></div>
          <div>Last Updated: <span className="font-mono">{(ret.updated_at || "").replace("T", " ").slice(0, 19)}</span></div>
          {ret.filed_at && <div>E-filed: <span className="font-mono">{ret.filed_at.replace("T", " ").slice(0, 19)}</span></div>}
        </div>

        {/* Signature */}
        <div className="mt-16 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="border-b border-black h-10" />
            <div className="text-xs text-slate-500 mt-1">Client signature</div>
          </div>
          <div>
            <div className="border-b border-black h-10" />
            <div className="text-xs text-slate-500 mt-1">Date</div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-4 border-t-2 border-black flex items-center justify-between text-[10px] text-slate-500">
          <span>Taxfree Filer LLP · CPA-supervised · Confidential</span>
          <span>Printed {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-semibold text-[#0A2540] mt-0.5">{value || "—"}</div>
    </div>
  );
}

function FinRow({ label, value, tone }) {
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 text-slate-600">{label}</td>
      <td className={`py-2 text-right font-mono tabular-nums ${tone}`}>${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
  );
}
